import { BotIcon, ExternalLinkIcon, MessageSquareIcon } from "lucide-react";
import { useId } from "react";
import type {
  CmmdForgeAutomationRun,
  CmmdTaskConversation,
  CmmdTaskConversationSummary,
} from "@cmmd-forge/contracts";

import type { ResolvedTaskThread, ResolvedTaskThreadHistoryGroup } from "~/lib/taskThreadLink";
import { resolveActivityTimestamp } from "~/lib/taskThreadLink";
import type { SidebarThreadSummary } from "~/types";
import { AgentStatusBadge } from "./atoms/AgentStatusBadge";
import {
  deriveTaskAgentOwnerPresentation,
  temporaryAgentName,
  type TaskAgentOwnerPresentation,
} from "./taskExecutionPresentation";
import { formatTaskDateTime } from "./taskDateTime";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { ProjectTaskConversationHistory } from "./ProjectTaskConversationHistory";
import { ProjectTaskAutomationHistory } from "./ProjectTaskAutomationHistory";
import { ProjectTaskAccountableAgent } from "./ProjectTaskAccountableAgent";

export interface ProjectTaskForgeActivityProps {
  readonly history: readonly ResolvedTaskThreadHistoryGroup[];
  readonly conversations: readonly CmmdTaskConversationSummary[];
  readonly conversationsNextCursor: string | null;
  readonly selectedConversation: CmmdTaskConversation | null;
  readonly expandedConversationId: string | null;
  readonly conversationsLoaded: boolean;
  readonly conversationsLoading: boolean;
  readonly loadingMoreConversations: boolean;
  readonly loadingConversationId: string | null;
  readonly loadingMoreConversationEvents: boolean;
  readonly conversationsError: string | null;
  readonly conversationDetailError: string | null;
  readonly runs: readonly CmmdForgeAutomationRun[];
  readonly loading: boolean;
  readonly onStartWork: () => void;
  readonly onRetryConversations: () => void;
  readonly onLoadMoreConversations: () => void;
  readonly onLoadConversation: (conversationId: string) => void;
  readonly onLoadMoreConversationEvents: () => void;
  readonly onClearConversation: () => void;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
}

export function ProjectTaskForgeActivity(props: ProjectTaskForgeActivityProps) {
  const owner = deriveTaskAgentOwnerPresentation(props.runs, props.history);
  const id = useId();
  return (
    <div className="space-y-6">
      <ProjectTaskConversationHistory
        conversations={props.conversations}
        nextCursor={props.conversationsNextCursor}
        selectedConversation={props.selectedConversation}
        expandedConversationId={props.expandedConversationId}
        loaded={props.conversationsLoaded}
        loading={props.conversationsLoading}
        loadingMore={props.loadingMoreConversations}
        loadingConversationId={props.loadingConversationId}
        loadingMoreEvents={props.loadingMoreConversationEvents}
        error={props.conversationsError}
        detailError={props.conversationDetailError}
        onRetry={props.onRetryConversations}
        onLoadMore={props.onLoadMoreConversations}
        onLoadConversation={props.onLoadConversation}
        onLoadMoreEvents={props.onLoadMoreConversationEvents}
        onClearConversation={props.onClearConversation}
        onOpenConversation={props.onStartWork}
      />
      <ForgeActivityContent
        loading={props.loading}
        conversationsSettled={props.conversationsLoaded || props.conversationsError !== null}
        conversationsFailed={props.conversationsError !== null}
        owner={owner}
        hasConversations={props.conversations.length > 0}
        history={props.history}
        runs={props.runs}
        idPrefix={id}
        onOpenThread={props.onOpenThread}
      />
    </div>
  );
}

function ForgeActivityContent(props: {
  readonly loading: boolean;
  readonly conversationsSettled: boolean;
  readonly conversationsFailed: boolean;
  readonly owner: TaskAgentOwnerPresentation | null;
  readonly hasConversations: boolean;
  readonly history: readonly ResolvedTaskThreadHistoryGroup[];
  readonly runs: readonly CmmdForgeAutomationRun[];
  readonly idPrefix: string;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
}) {
  const hasRuntimeActivity = props.history.length > 0 || props.runs.length > 0;
  if (props.loading) {
    return <p className="text-sm text-muted-foreground">Loading Forge activity…</p>;
  }
  if (shouldShowEmptyForgeActivity(props, hasRuntimeActivity)) {
    return <p className="text-sm text-muted-foreground">No Forge activity yet.</p>;
  }
  if (!hasRuntimeActivity) return null;
  return (
    <>
      {props.owner !== null ? (
        <ProjectTaskAccountableAgent
          owner={props.owner}
          headingId={`${props.idPrefix}-accountable-agent`}
        />
      ) : null}
      {props.history.length > 0 ? (
        <ThreadHistory
          history={props.history}
          headingId={`${props.idPrefix}-threads`}
          onOpenThread={props.onOpenThread}
        />
      ) : null}
      {props.runs.length > 0 ? (
        <ProjectTaskAutomationHistory runs={props.runs} headingId={`${props.idPrefix}-runs`} />
      ) : null}
    </>
  );
}

function shouldShowEmptyForgeActivity(
  props: {
    readonly conversationsSettled: boolean;
    readonly conversationsFailed: boolean;
    readonly hasConversations: boolean;
  },
  hasRuntimeActivity: boolean,
): boolean {
  return (
    props.conversationsSettled &&
    !props.conversationsFailed &&
    !hasRuntimeActivity &&
    !props.hasConversations
  );
}

function ThreadHistory(props: {
  readonly history: readonly ResolvedTaskThreadHistoryGroup[];
  readonly headingId: string;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
}) {
  return (
    <section aria-labelledby={props.headingId} className="space-y-2">
      <h3 id={props.headingId} className="text-sm font-semibold text-foreground">
        Execution threads
      </h3>
      <ol className="divide-y divide-border border-y border-border">
        {props.history.map((group) => (
          <li key={group.thread.thread.id} className="py-1">
            <ThreadRow resolved={group.thread} onOpenThread={props.onOpenThread} variant="thread" />
            {group.workers.length > 0 ? (
              <div className="ml-8 border-l border-border pl-3">
                <p className="pt-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  Temporary contributors ({group.workers.length})
                </p>
                <ol>
                  {group.workers.map((worker) => (
                    <li key={worker.thread.id}>
                      <ThreadRow
                        resolved={worker}
                        onOpenThread={props.onOpenThread}
                        variant="worker"
                      />
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

function ThreadRow(props: {
  readonly resolved: ResolvedTaskThread;
  readonly variant: "thread" | "worker";
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
}) {
  const thread = props.resolved.thread;
  const Icon = props.variant === "worker" ? BotIcon : MessageSquareIcon;
  const displayTitle =
    props.variant === "worker" ? temporaryAgentName(thread.id) : thread.title || "Untitled thread";
  const subtitle = [
    props.variant === "worker" ? "Temporary AI agent" : null,
    props.variant === "worker" && thread.workerRole ? humanize(thread.workerRole) : null,
    props.variant === "worker" ? thread.title : null,
    `Thread ${thread.id.slice(0, 8)}`,
    thread.branch,
    formatTaskDateTime(resolveActivityTimestamp(thread)),
  ]
    .filter((value): value is string => Boolean(value))
    .join(" · ");

  return (
    <button
      type="button"
      title={`Forge thread ${thread.id}`}
      onClick={() => props.onOpenThread(thread)}
      className={`${RESPONSIVE_TEXT_BUTTON_CLASS} group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-11 md:rounded-md`}
    >
      <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className="truncate text-sm font-medium text-foreground">{displayTitle}</span>
          <AgentStatusBadge
            agentStatus={props.resolved.agentStatus}
            agentProvider={props.resolved.agentProvider}
          />
          {thread.archivedAt ? (
            <span className="text-[11px] text-muted-foreground">Archived</span>
          ) : null}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">{subtitle}</span>
      </span>
      <ExternalLinkIcon
        className="size-3.5 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        aria-hidden
      />
    </button>
  );
}

function humanize(value: string): string {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
